import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface SubRouteType {
  id: string;
  type: string;
  name: string;
  isDeleted: boolean;
  effectiveDate: Date;
  createdDate: Date;
  percentage0: number;
  percentage1: number;
  percentage2: number;
  percentage3: number;
  conditionValues: ConditionValueType[];
  routingPlanId: string;
  provider1Id: string;
  provider2Id: string;
  provider3Id: string;
  operatorId: number;
  providerId: string;
  version: number;
  createdBy: string;
  position?: string;
  updatedBy?: string;
}
export interface SubRoutingHistory {
  id: string;
  snapshot: SubRouteType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}
export interface SubRoutingHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: SubRouteType & { timestamp: string };
  diff?: Record<string, string>;
}

export interface ConditionValueType {
  filterType: string;
  conditionType: "CONTAINS" | string;
  value: string;
}

export const exportSubRoute = async (
  params: Params<SubRouteType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/sub-routing/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "SubRoute.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching SubRoute :",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchSubRoutings(
  params: Params<SubRouteType>
): Promise<IPageable<SubRouteType>> {
  try {
    const response = await axiosInstance.get("/sub-routing", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchSubRouting(
  id: SubRouteType["id"]
): Promise<SubRouteType> {
  try {
    const response = await axiosInstance.get(`/sub-routing/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`
    );
  }
}

export const addSubRoutingApi = async (data: Partial<SubRouteType>) => {
  try {
    const response = await axiosInstance.post("/sub-routing", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the sub-routing."
    );
  }
};
export const SubRoutingBulkApi = async (data: Partial<SubRouteType>[]) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/sub-routing", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the sub-routing."
    );
  }
};

export const SubRoutingCreateAllApi = async (data: Partial<SubRouteType>[]) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.post("/sub-routing/all", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the sub-routing."
    );
  }
};

export const editSubRoutingApi = async (
  id: string,
  data: Partial<SubRouteType>
) => {
  try {
    const response = await axiosInstance.put(`/sub-routing/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the sub-routing."
    );
  }
};

export const getSubRoutingById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/sub-routing/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the sub-routing."
    );
  }
};

export const deleteSubRoutingApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete("/sub-routing", { data: ids });
    console.log(
      `Sub‑routings with IDs [${ids.join(", ")}] deleted successfully.`
    );
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the sub‑routings."
    );
  }
};

export const useSubRoutingQuery = (
  params: Params<SubRouteType>
): UseQueryResult<IPageable<SubRouteType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["subRouting", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchSubRoutings(effectiveParams),
  });
};

export async function fetchSubRoutingHistory(
  id: SubRouteType["id"],
  params: Params<SubRouteType>
): Promise<IPageable<SubRouteType>> {
  try {
    const response = await axiosInstance.get(`/instructions/history`, {
      params: { mainId: id, ...params, sort: "historyTimestamp,desc" },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the sub Routing history for ID ${id}.`
    );
  }
}
